import { TherapistAvailabilityDto, TherapistDayDto } from '../../../../shared/dtos/therapist.dto';
import { FC, ReactElement } from 'react';
import i18next from 'i18next';
import { Col, Row } from 'react-bootstrap';
import Table from 'react-bootstrap/Table';
import { typedEntries } from '../../util/typeUtils';

type AvailabilityTableProps = {
  availability: TherapistAvailabilityDto,
  onChange: (next: TherapistAvailabilityDto) => void,
  availabilityError?: string,
}

function formatTime(time: number): string {
  if (!Number.isInteger(time) || time < 0 || time > 2400) {
    return '00:00';
  }

  const hours: number = Math.floor(time / 100);
  const minutes: number = time % 100;

  if (minutes >= 60) {
    return '00:00';
  }

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

function removeTimeSlot(
  availability: TherapistAvailabilityDto,
  key: keyof TherapistAvailabilityDto,
  index: number
): TherapistAvailabilityDto {
  const daySlots: TherapistDayDto[] = [...availability[key]];
  daySlots.splice(index, 1);
  return {
    ...availability,
    [key]: daySlots
  };
}

function addTimeSlot(availability: TherapistAvailabilityDto, key: keyof TherapistAvailabilityDto): TherapistAvailabilityDto {
  const daySlots: TherapistDayDto[] = [...availability[key]];
  let lastEndTime: number = 900;
  let newEndTime: number = 1700;

  if (daySlots.length > 0) {
    lastEndTime = daySlots[daySlots.length - 1].endTime;
    newEndTime = lastEndTime + 400;
  }

  daySlots.push({
    startTime: lastEndTime,
    endTime: newEndTime > 2400 ? 0 : newEndTime,
  });

  return {
    ...availability,
    [key]: daySlots
  };
}

function updateTimeSlot(
  availability: TherapistAvailabilityDto,
  key: keyof TherapistAvailabilityDto,
  index: number,
  action: keyof TherapistDayDto,
  value: string,
): TherapistAvailabilityDto {
  if (!value) {
    return availability;
  }

  const numberValue: number = parseInt(value.replace(/:/g, ''), 10);
  if (Number.isNaN(numberValue) || numberValue < 0 || numberValue > 2400 || numberValue % 100 >= 60) {
    return availability;
  }

  const daySlots: TherapistDayDto[] = [...availability[key]];
  daySlots[index] = { ...daySlots[index], [action]: numberValue };

  return {
    ...availability,
    [key]: daySlots
  };
}

const AvailabilityTable: FC<AvailabilityTableProps> = ({
  availability,
  onChange,
  availabilityError,
}: AvailabilityTableProps): ReactElement => {
  const hasAvailabilityError: boolean = !!availabilityError;

  const handleRemove = (key: keyof TherapistAvailabilityDto, index: number): void => {
    const next: TherapistAvailabilityDto = removeTimeSlot(availability, key, index);
    onChange(next);
  };

  const handleAdd = (key: keyof TherapistAvailabilityDto): void => {
    const next: TherapistAvailabilityDto = addTimeSlot(availability, key);
    onChange(next);
  };

  const handleTimeChange = (key: keyof TherapistAvailabilityDto, index: number, newValue: string, field: keyof TherapistDayDto): void => {
    const next: TherapistAvailabilityDto = updateTimeSlot(availability, key, index, field, newValue);
    onChange(next);
  };

  return (
    <>
      <h2 className="fw-semibold mb-2 mt-4">
        {i18next.t('therapistSettings.availability')}
      </h2>

      <Row>
        <Col md={6}>
          <div className={`d-inline-block w-100 border rounded p-3 ${hasAvailabilityError ? 'border-danger' : 'border'}`}>
            <Table className="mb-0">
              <tbody>
                {typedEntries(availability).map(([key, slots]) => (
                  <tr key={key}>
                    <td className="w-10 align-top">
                      {i18next.t(`generic.days.${key}`)}
                    </td>
                    <td className="w-50">
                      {slots.map((slot, index) => {
                        const isInvalid: boolean = slot.startTime > slot.endTime;

                        return (
                          <div key={`${key}-${index}`} className="mb-2">
                            <div className="d-flex align-items-center gap-2">
                              <input
                                className={`form-control text-center ${isInvalid ? 'is-invalid' : ''}`}
                                type="time"
                                value={formatTime(slot.startTime)}
                                onChange={(event) => handleTimeChange(key, index, event.target.value, 'startTime')}
                              />

                              <span>-</span>

                              <input
                                className={`form-control text-center ${isInvalid ? 'is-invalid' : ''}`}
                                type="time"
                                value={formatTime(slot.endTime)}
                                onChange={(event) => handleTimeChange(key, index, event.target.value, 'endTime')}
                              />

                              <button
                                type="button"
                                className="btn btn-link p-0 text-danger"
                                onClick={() => handleRemove(key, index)}
                              >
                                <i className="fas fa-trash" />
                              </button>
                            </div>

                            {isInvalid && (
                              <div className="text-danger small mt-1">
                                {i18next.t('therapistSettings.invalidTime')}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </td>

                    <td className="w-10 text-end align-top">
                      {slots.length < 3 && (
                        <button
                          type="button"
                          className="btn p-0 border-0"
                          onClick={() => handleAdd(key)}
                        >
                          <i className="fas fa-add" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {hasAvailabilityError && (
            <div className="text-danger small mt-1">
              {availabilityError}
            </div>
          )}

        </Col>
      </Row>
    </>
  );
};

export default AvailabilityTable;
